import { useStyle } from '@/classify';
import Button from '@/components/Button';
import Field from '@/components/Field';
import FormError from '@/components/FormError/formError';
import GoogleRecaptcha from '@/components/GoogleReCaptcha';
import Password from '@/components/Password';
import TextInput from '@/components/TextInput';
import { useSignIn } from '@/hooks/SignIn/useSignIn';
import { isRequired, validateEmail } from '@/util/formValidators';
import { Form } from 'informed';
import { FormattedMessage, useIntl } from 'react-intl';
import { GET_CART_DETAILS_QUERY } from './signIn.gql';
import defaultClasses from './signIn.module.css';

const SignIn = props => {
  const classes = useStyle(defaultClasses, props.classes);

  const {
    setDefaultUsername = () => {},
    showCreateAccount = () => {},
    showForgotPassword = () => {},
    onLoginSuccess = () => {},
    initialValues,
  } = props;

  const { formatMessage } = useIntl();

  const talonProps = useSignIn({
    getCartDetailsQuery: GET_CART_DETAILS_QUERY,
    setDefaultUsername,
    showCreateAccount,
    showForgotPassword,
    onLoginSuccess,
  });

  const {
    errors,
    signinHandleEnterKeyPress,
    handleForgotPassword,
    handleSubmit,
    isBusy,
    formApiRef,
    recaptchaWidgetProps,
    isSigningIn,
  } = talonProps;

  return (
    <div
      data-cy='SignIn-root'
      className={classes.root}
    >
      <div className={classes.container}>
        <p className='text-center  font-poppins text-[20px] font-bold uppercase text-primary lg_text-left lg_text-[30px]'>
          sign in
        </p>
        <p className={classes.subTitle}>If you have an account with us, please log in.</p>
        <FormError
          errors={Array.from(errors.values())}
          allowErrorMessages={true}
        />
        <Form
          formApiRef={formApiRef}
          className={classes.form}
          onSubmit={handleSubmit}
          data-cy='SignIn-form'
          initialValues={initialValues && initialValues}
        >
          <Field
            id='email'
            label={formatMessage({
              id: 'signIn.emailAddressText',
              defaultMessage: 'Email address',
            })}
            classes={{
              requiredLabel: classes.requiredLabel,
            }}
            required
          >
            <TextInput
              id='email'
              data-cy='SignIn-email'
              autoComplete='email'
              field='email'
              validate={value => {
                return validateEmail(typeof value === 'string' ? value.trim() : value);
              }}
              aria-label={formatMessage({
                id: 'global.emailRequired',
                defaultMessage: 'Email Required',
              })}
            />
          </Field>
          <div className={classes.passwordContainer}>
            <div className={classes.passwordHeader}>
              <label className={classes.passwordLabel}>Password</label>
            </div>
            <Password
              data-cy='SignIn-password'
              fieldName='password'
              id='Password'
              required
              validate={isRequired}
              autoComplete='current-password'
              isToggleButtonHidden={false}
              aria-label={formatMessage({
                id: 'global.passwordRequired',
                defaultMessage: 'Password Required',
              })}
            />
          </div>
          <GoogleRecaptcha {...recaptchaWidgetProps} />
          <div className={classes.buttonsContainer}>
            <Button
              priority='high'
              type='submit'
              onKeyDown={signinHandleEnterKeyPress}
              data-cy='SignInButton-root_highPriority'
              className={classes.submitButton}
              disabled={isBusy}
              loading={isSigningIn}
            >
              <FormattedMessage
                id={'signIn.signInText'}
                defaultMessage={'Sign In'}
              />
            </Button>
          </div>
          <div className={classes.footer}>
            <span
              className={classes.forgotPasswordLink}
              onClick={handleForgotPassword}
            >
              Forgot Your Password?
            </span>
          </div>
        </Form>
      </div>
    </div>
  );
};

export default SignIn;
